import { useCallback, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { useClickAway } from 'react-use';
import DialogsAPI from 'api/DialogsAPI';
import TrackingAPI from 'api/TrackingAPI';
import retry_icon from 'assets/rotate.svg?url';
import SuggestionButton from 'assets/suggestionButton.svg?react';
import { useMappedState } from 'redux-react-hook';
import { useDispatch } from 'redux-react-hook';
import { setPremiumUsage } from 'store/DEPRECATEDusers/actions';
import styled from 'styled-components';
import Spinner from 'ui-kit/Spinner';
import { colors } from 'ui-kit/theme/colors';

import 'react-tooltip/dist/react-tooltip.css';

interface ISuggestionButtonProps {
  dialogId: number;
  setNewMessageText: (newMessage: string) => void;
  onClickCheck: () => boolean;
  suggestionUsage: number;
  isOpenSuggestionMenu: boolean;
  isInitialSuggestionTooltipOpen: boolean;
  setIsOpenSuggestionMenu: (isOpen: boolean) => void;
}

interface Message {
  text: string;
  textType: string;
}

interface MessagePart {
  content: {
    parts: Message[];
  };
}

interface DialogResult {
  'new-messages': MessagePart[];
}

interface DialogData {
  results: DialogResult[];
}

enum MessageTextType {
  SPEECH = 'speech',
  COMMENT = 'comment'
}

const NUMBER_OF_TRIALS = 2;

export const SuggestionsButton = ({
  dialogId,
  setNewMessageText,
  onClickCheck,
  suggestionUsage,
  isInitialSuggestionTooltipOpen,
  isOpenSuggestionMenu,
  setIsOpenSuggestionMenu
}: ISuggestionButtonProps) => {
  const dispatch = useDispatch();

  const { currentUserId, hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUserId = state.users.currentUserId;
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = currentUser?.hasSubscription == true;
      return {
        currentUserId,
        hasSubscription
      };
    }, [])
  );


  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [newParts, setNewParts] = useState<Message[][]>([]);

  const modalRef = useRef<HTMLDivElement>(null);

  const isTrial = !hasSubscription && suggestionUsage < NUMBER_OF_TRIALS;

  const transformMessages = (messages: Message[]) => {
    return messages
      .map(message =>
        message.textType === MessageTextType.COMMENT ? `[${message.text}]` : message.text
      )
      .join(' ');
  };

  const fetchDialogSuggestions = async () => {
    try {
      const data: DialogData = await DialogsAPI.getDialogSuggestionsMulti(dialogId, currentUserId);

      if (!data) {
        throw new Error();
      }

      const parts = data.results.map((result: DialogResult) => {
        return result['new-messages'][0].content.parts;
      });

      return parts;
    } catch (_err) {
      throw new Error();
    }
  };

  const handleSetSuggestionUsage = () => {
    dispatch(setPremiumUsage({ suggestions: suggestionUsage + 1 }));
  };

  const handleSetNewMessages = (newMessages: Message[]) => {
    setNewMessageText(transformMessages(newMessages));
    setIsOpenSuggestionMenu(false);
  };

  const handleOpenSuggestionModal = async () => {
    const isBlocked = onClickCheck();

    if (isBlocked) {
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setIsOpenSuggestionMenu(true);

    TrackingAPI.trackEvent('user_suggestion');

    try {
      const newParts = await fetchDialogSuggestions();
      handleSetSuggestionUsage();
      setNewParts(newParts);
    } catch (_err) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSuggestionModal = () => {
    setIsOpenSuggestionMenu(false);
  };

  useClickAway(modalRef, () => {
    if (isOpenSuggestionMenu) {
      handleCloseSuggestionModal();
    }
  });

  const SuggestionModal = () => {
    return (
      <ModalWrapper>
        <div ref={modalRef}>
          <ModalContent>
            {isError && (
              <ErrorContainer>
                An error occurred. Please try again.{' '}
                <RegenerateImage
                  alt={'regenerate suggestion'}
                  src={retry_icon}
                  onClick={handleOpenSuggestionModal}
                />
              </ErrorContainer>
            )}

            {!isError && isLoading && (
              <LoadingContainer>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ marginBottom: '10px', color: 'rgba(169, 169, 169, 1)' }}>
                    Generating suggestion...
                  </span>
                  <Spinner size={20} />
                </div>
              </LoadingContainer>
            )}

            {!isLoading && !isError && (
              <PartsWrapper>
                {newParts.map(newMessages => (
                  <PartsContainer onClick={() => handleSetNewMessages(newMessages)}>
                    {newMessages.map(message => (
                      <SuggestionContainer key={message.text}>
                        {message.textType === MessageTextType.SPEECH ? (
                          <WhiteText>{message.text}&nbsp;&nbsp;</WhiteText>
                        ) : (
                          <ItalicGrayText key={message.text}>{message.text}&nbsp;&nbsp;</ItalicGrayText>
                        )}
                      </SuggestionContainer>
                    ))}
                  </PartsContainer>
                ))}
              </PartsWrapper>
            )}
          </ModalContent>
        </div>
      </ModalWrapper>
    );
  };

  return (
    <>
      {isOpenSuggestionMenu && <SuggestionModal />}
      <Wrapper>
        {isTrial && suggestionUsage < NUMBER_OF_TRIALS && (
          <SuggestionUsage>{NUMBER_OF_TRIALS - suggestionUsage}</SuggestionUsage>
        )}
        <SuggestionButton
          onClick={handleOpenSuggestionModal}
          data-tooltip-id="suggestion-button"
          className="suggestion-button"
        />
        {!isInitialSuggestionTooltipOpen && (
          <Tooltip
            id="suggestion-button"
            place="top-end"
            className="shadow-lg"
            opacity={1}
            // @ts-ignore
            content={
              <p className='max-w-[150px] font-["Nunito_Sans"]'>
                Get engaging reply suggestions to advance your story
              </p>
            }
          />
        )}
      </Wrapper>
    </>
  );
};

const PartsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 40vh;
  padding-right: 10px;
  overflow: auto;
`;

const PartsContainer = styled.div<React.HTMLProps<HTMLDivElement>>`
  background-color: rgba(70, 55, 50, 1);
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;

  :hover {
    opacity: 0.8;
  }
`;

const SuggestionUsage = styled.div`
  position: absolute;
  bottom: 6px;
  right: -8px;
  background-color: ${colors.error};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid rgba(29, 29, 29, 1);
  font-size: 12px;
`;

const SuggestionContainer = styled.div<React.HTMLProps<HTMLDivElement>>`
  cursor: pointer;
  display: inline;
`;

const RegenerateImage = styled.img<React.HTMLProps<HTMLImageElement>>`
  cursor: pointer;
  opacity: 1;
  min-height: 16px;
  min-width: 16px;

  :hover {
    opacity: 0.8;
  }
`;

const ItalicGrayText = styled.div`
  font-style: italic;
  color: rgb(169, 169, 169);
  display: inline;
`;

const WhiteText = styled.div`
  color: rgb(255, 255, 255);
  display: inline;
`;

const LoadingContainer = styled.div`
  width: 186px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;

  color: #fa1e1e;
  padding: 5px;
  border-radius: 5px;
  text-align: center;
`;

const ModalContent = styled.div`
  background: #100d0b;
  border: 1px solid #1d1d1d;
  border-radius: 12px;
  box-shadow: 0px 0px 40px 0px #00000066;
  padding: 16px;
`;

const ModalWrapper = styled.div`
  position: absolute;
  bottom: 70px;
  left: 30px;
  z-index: 10;

  @media (max-width: 720px) {
    left: 15px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 16px;

  svg {
    cursor: pointer;
  }
`;
